import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import type { DialogProps } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { SuperSearchPageResult, SuperSearchResult } from 'api';
import { FULL_ACCESS_VIEWER_VALUE } from 'common/constants/user';
import { normalizePinnedItems, PINNED_COMPANIES_KEY, PINNED_FIRMS_KEY } from 'common/constants/userPreferences';
import type { PinnedItemId } from 'common/constants/userPreferences';
import { VALUATIONS_SLUG } from 'common/constants/valuations';
import { useExtraPageActionVisibility, useFirmScopedPermissions, useLocalStorageByUser } from 'common/hooks';
import { useStore } from 'common/store';
import type { FirmCompany } from 'common/types/company';
import type { UseStoreValues } from 'common/types/store';
import useCheckFeatures from 'components/FeatureEnabler/hooks';
import { useGuidedTour } from 'components/ui';
import { useDebouncedValue } from 'hooks';
import { PrismThemeProvider } from 'theme';
import { useUserUtils } from 'utilities/userUtils';
import { getSuperSearchCommandSections } from './SuperSearch.commands';
import { SUPER_SEARCH_MIN_REMOTE_SEARCH_LENGTH, SUPER_SEARCH_NAVIGATION_DELAY_MS } from './SuperSearch.constants';
import { getSuperSearchQuickAccessItems, SUPER_SEARCH_QUICK_ACCESS_LIMIT } from './SuperSearch.history';
import { canScopeItem, getNavigationContext, getResultTone, getResultTypeLabel } from './SuperSearch.presentation';
import { LoadingProgress, StyledDialog } from './SuperSearch.styles';
import { SUPER_SEARCH_TEST_IDS } from './SuperSearch.testIds';
import type {
  SuperSearchContext,
  SuperSearchDisplayItem,
  SuperSearchPendingNavigation,
  SuperSearchPendingPage,
  SuperSearchProps,
  SuperSearchShortcut,
} from './SuperSearch.types';
import {
  getGhostCompletion,
  getSuperSearchNavigationPath,
  getSuperSearchSections,
  isSuperSearchSavedDestinationResult,
  matchesSuperSearchQuery,
  mergeSuperSearchResults,
} from './SuperSearch.utils';
import SuperSearchFooter from './SuperSearchFooter';
import SuperSearchHeader from './SuperSearchHeader';
import SuperSearchNavigationStatus from './SuperSearchNavigationStatus';
import SuperSearchResults from './SuperSearchResults';
import useSuperSearchActions from './useSuperSearchActions';
import useSuperSearchErrorState from './useSuperSearchErrorState';
import useSuperSearchHistory from './useSuperSearchHistory';
import useSuperSearchLocalResults from './useSuperSearchLocalResults';
import useSuperSearchQuery from './useSuperSearchQuery';

const SEARCH_DEBOUNCE_MS = 300;
const MIN_DOCUMENT_SEARCH_LENGTH = 4;
const MIN_SAVED_DESTINATION_SEARCH_LENGTH = 3;
const QUICK_ACCESS_RESULTS_PER_SECTION = 2;
const SuperSearch = ({ authorizationRevision, initialContext, onClose, open, zoomLevel }: SuperSearchProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { availableTours, startTour } = useGuidedTour();
  const { isStaffUser, isSuperUser } = useUserUtils();
  const [{ companyList, companyMeasurementDateList, currentMeasurementDate, firmList }] =
    useStore() as unknown as UseStoreValues;
  const {
    canAccessAuditLogs,
    canAccessFirmSettings,
    canAccessUserManagement,
    canCreateCompany,
    canCreateMeasurementDate,
    canMutateFirmScopedEntities,
  } = useFirmScopedPermissions();
  const actionVisibility = useExtraPageActionVisibility({
    bulkActions: canMutateFirmScopedEntities,
    pdfExport: isStaffUser() || isSuperUser(),
  });
  const [, checkFeature] = useCheckFeatures();
  const [context, setContext] = useState<SuperSearchContext>(initialContext);
  const {
    canMutateFirmScopedEntities: canMutateCompany,
    currentFirmRole: companyFirmRole,
    isRestrictedUser: isCompanyFirmRestricted,
  } = useFirmScopedPermissions({ firmId: context.firm?.id });
  const canOpenProForma =
    canMutateCompany || (!isCompanyFirmRestricted && companyFirmRole === FULL_ACCESS_VIEWER_VALUE);
  const [pendingPage, setPendingPage] = useState<SuperSearchPendingPage>();
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [isKeyboardNavigation, setIsKeyboardNavigation] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<SuperSearchPendingNavigation>();
  const [pressedShortcut, setPressedShortcut] = useState<SuperSearchShortcut>();
  const [pinnedCompaniesRaw] = useLocalStorageByUser<PinnedItemId[]>(PINNED_COMPANIES_KEY, []);
  const [pinnedFirmsRaw] = useLocalStorageByUser<PinnedItemId[]>(PINNED_FIRMS_KEY, []);
  const {
    clearHistory: clearSuperSearchHistory,
    history: superSearchHistory,
    isReady: isSuperSearchHistoryReady,
    recordItem: recordSuperSearchHistoryItem,
  } = useSuperSearchHistory();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const navigationCancelRef = useRef<HTMLButtonElement>(null);
  const navigationTimerRef = useRef<number | null>(null);
  const shortcutFeedbackTimerRef = useRef<number | null>(null);
  const cancelPendingNavigation = useCallback(() => {
    if (navigationTimerRef.current !== null) {
      window.clearTimeout(navigationTimerRef.current);
      navigationTimerRef.current = null;
    }

    setPendingNavigation(undefined);
  }, []);
  const flashShortcut = useCallback((shortcut: SuperSearchShortcut) => {
    if (shortcutFeedbackTimerRef.current !== null) {
      window.clearTimeout(shortcutFeedbackTimerRef.current);
    }

    setPressedShortcut(shortcut);
    shortcutFeedbackTimerRef.current = window.setTimeout(() => {
      setPressedShortcut(undefined);
      shortcutFeedbackTimerRef.current = null;
    }, 160);
  }, []);
  const pinnedCompanyIds = useMemo(
    () => new Set(normalizePinnedItems(pinnedCompaniesRaw).map(item => item.value)),
    [pinnedCompaniesRaw]
  );
  const pinnedFirmIds = useMemo(
    () => new Set(normalizePinnedItems(pinnedFirmsRaw).map(item => item.value)),
    [pinnedFirmsRaw]
  );
  const normalizedQuery = query.trim();
  const debouncedQuery = useDebouncedValue(normalizedQuery, SEARCH_DEBOUNCE_MS);
  const {
    activeMeasurementDate,
    firmIconUrlsById,
    localEntityResults,
    localMeasurementDateResults,
    localPageResults,
    quickAccessEntityResults,
    quickAccessMeasurementDateResults,
    quickAccessPageResults,
  } = useSuperSearchLocalResults({
    checkFeature,
    companyList,
    companyMeasurementDateList,
    context,
    currentMeasurementDate,
    firmList,
    initialContext,
    locationSearch: location.search,
    normalizedQuery,
    pendingPage,
    pinnedCompanyIds,
    pinnedFirmIds,
  });
  const localResultIds = useMemo(
    () =>
      new Set([...localEntityResults, ...localMeasurementDateResults, ...localPageResults].map(result => result.id)),
    [localEntityResults, localMeasurementDateResults, localPageResults]
  );
  const localPageTypes = useMemo(() => new Set(localPageResults.map(result => result.type)), [localPageResults]);
  const quickAccessResults = useMemo(
    () => mergeSuperSearchResults(quickAccessPageResults, quickAccessMeasurementDateResults, quickAccessEntityResults),
    [quickAccessEntityResults, quickAccessMeasurementDateResults, quickAccessPageResults]
  );
  const quickAccessResultIds = useMemo(
    () => new Set(quickAccessResults.map(result => result.id)),
    [quickAccessResults]
  );
  const quickAccessPageTypes = useMemo(
    () => new Set(quickAccessPageResults.map(result => result.type)),
    [quickAccessPageResults]
  );
  const apiQuery = [pendingPage?.searchLabel ?? pendingPage?.label, debouncedQuery].filter(Boolean).join(' ');
  const requestKey = [pendingPage?.searchLabel ?? pendingPage?.label, normalizedQuery].filter(Boolean).join(' ');
  const isQueryDebouncing = normalizedQuery !== debouncedQuery;
  const isSearchEnabled = open && !isQueryDebouncing && apiQuery.length >= SUPER_SEARCH_MIN_REMOTE_SEARCH_LENGTH;
  const includeDocuments = !pendingPage && debouncedQuery.length >= MIN_DOCUMENT_SEARCH_LENGTH;
  const includeSavedDestinations = !pendingPage && debouncedQuery.length >= MIN_SAVED_DESTINATION_SEARCH_LENGTH;
  const search = useSuperSearchQuery(apiQuery, isSearchEnabled, {
    authorizationRevision,
    contextCompanyId: context.company?.id,
    contextFirmId: context.firm?.id,
    contextMeasurementDate: activeMeasurementDate,
    includeSavedDestinations,
    includeDocuments,
    requestKey,
  });

  // Rehydrate the route context and clear transient search state every time the palette opens.
  useEffect(() => {
    if (!open) {
      cancelPendingNavigation();
      return;
    }

    cancelPendingNavigation();
    setContext(initialContext);
    setPendingPage(undefined);
    setQuery('');
    setSelectedIndex(-1);
    setIsKeyboardNavigation(false);
    setPressedShortcut(undefined);
  }, [cancelPendingNavigation, initialContext, open]);

  // Clear delayed navigation and key feedback after the command palette unmounts.
  useEffect(
    () => () => {
      if (navigationTimerRef.current !== null) {
        window.clearTimeout(navigationTimerRef.current);
      }
      if (shortcutFeedbackTimerRef.current !== null) {
        window.clearTimeout(shortcutFeedbackTimerRef.current);
      }
    },
    []
  );

  const results = useMemo(() => {
    const availableResults = isSearchEnabled
      ? search.data?.results.filter(result => {
        if (!search.isFetching && !search.isError) {
          return true;
        }

        if (includeDocuments && result.type === SuperSearchResult.type.DOCUMENT) {
          return false;
        }

        return !includeSavedDestinations || !isSuperSearchSavedDestinationResult(result);
      })
      : undefined;
    const visibleResults = availableResults?.filter(
      result =>
        matchesSuperSearchQuery(normalizedQuery, result.search_text) &&
        (!result.page?.required_feature_flag || checkFeature(result.page.required_feature_flag))
    );
    const mergedResults = mergeSuperSearchResults(
      localPageResults,
      localEntityResults,
      localMeasurementDateResults,
      visibleResults
    );

    // Keep immediate navigation stable, then preserve API relevance while promoting pinned entities.
    return mergedResults.sort((left, right) => {
      const isLeftLocal = localResultIds.has(left.id);
      const isRightLocal = localResultIds.has(right.id);
      if (isLeftLocal !== isRightLocal) {
        return Number(isRightLocal) - Number(isLeftLocal);
      }

      const isLeftPinned = left.company ? pinnedCompanyIds.has(left.company.id) : pinnedFirmIds.has(left.firm.id);
      const isRightPinned = right.company ? pinnedCompanyIds.has(right.company.id) : pinnedFirmIds.has(right.firm.id);

      return Number(isRightPinned) - Number(isLeftPinned);
    });
  }, [
    checkFeature,
    includeDocuments,
    includeSavedDestinations,
    isSearchEnabled,
    localEntityResults,
    localMeasurementDateResults,
    localPageResults,
    localResultIds,
    normalizedQuery,
    pinnedCompanyIds,
    pinnedFirmIds,
    search.data?.results,
    search.isError,
    search.isFetching,
  ]);
  const resultSections = useMemo(
    () =>
      getSuperSearchSections({
        activeMeasurementDate,
        context,
        localPageTypes,
        localResultIds,
        pendingPage,
        query: debouncedQuery,
        results,
      }),
    [activeMeasurementDate, context, debouncedQuery, localPageTypes, localResultIds, pendingPage, results]
  );
  const commandSections = useMemo(
    () =>
      getSuperSearchCommandSections({
        availableTours,
        companyPermissions: {
          canMutateCompany,
          canOpenProForma,
        },
        context,
        firmPermissions: {
          canAccessAuditLogs,
          canAccessFirmSettings,
          canAccessUserManagement,
          canCreateCompany,
          canCreateMeasurementDate,
          canExportExcel: actionVisibility.excelExport,
          canExportPdf: actionVisibility.pdfExport,
          canRunBulkActions: actionVisibility.bulkActions,
        },
        hasCompanies: Array.isArray(companyList) && companyList.length > 0,
        routeFirm: initialContext.firm,
        pendingPage: Boolean(pendingPage),
        query: normalizedQuery,
      }),
    [
      canAccessAuditLogs,
      canAccessFirmSettings,
      canAccessUserManagement,
      canCreateCompany,
      canCreateMeasurementDate,
      actionVisibility.bulkActions,
      actionVisibility.excelExport,
      actionVisibility.pdfExport,
      availableTours,
      canMutateCompany,
      canOpenProForma,
      companyList,
      context,
      initialContext.firm,
      normalizedQuery,
      pendingPage,
    ]
  );
  const matchingSections = useMemo(() => {
    const navigationSections =
      context.firm && !context.company
        ? [
          ...resultSections.filter(section => section.id === 'companies'),
          ...resultSections.filter(section => section.id !== 'companies'),
        ]
        : resultSections;

    // Preserve navigation-first discovery; commands remain searchable without displacing companies and pages.
    return [...navigationSections, ...commandSections];
  }, [commandSections, context.company, context.firm, resultSections]);
  const matchingItems = useMemo(() => matchingSections.flatMap(section => section.items), [matchingSections]);
  const quickAccessSections = useMemo(() => {
    const previewSections = getSuperSearchSections({
      activeMeasurementDate,
      context,
      localPageTypes: quickAccessPageTypes,
      localResultIds: quickAccessResultIds,
      pendingPage,
      query: '',
      results: quickAccessResults,
    });
    // Reconstruct history/pins from the authorized empty-query pool, not typed matches.
    // Otherwise a completed no-match search would pass empty candidates and drop recent destinations.
    const authorizedCommandSections = getSuperSearchCommandSections({
      availableTours,
      companyPermissions: {
        canMutateCompany,
        canOpenProForma,
      },
      context,
      firmPermissions: {
        canAccessAuditLogs,
        canAccessFirmSettings,
        canAccessUserManagement,
        canCreateCompany,
        canCreateMeasurementDate,
        canExportExcel: actionVisibility.excelExport,
        canExportPdf: actionVisibility.pdfExport,
        canRunBulkActions: actionVisibility.bulkActions,
      },
      hasCompanies: Array.isArray(companyList) && companyList.length > 0,
      routeFirm: initialContext.firm,
      pendingPage: Boolean(pendingPage),
      query: '',
    });
    const authorizedCandidates = [
      ...previewSections.flatMap(section => section.items),
      ...authorizedCommandSections.flatMap(section => section.items),
    ];
    const previewItems = previewSections
      .flatMap(section => section.items.slice(0, QUICK_ACCESS_RESULTS_PER_SECTION))
      .slice(0, SUPER_SEARCH_QUICK_ACCESS_LIMIT);
    const quickAccessItems = getSuperSearchQuickAccessItems({
      candidates: authorizedCandidates,
      context,
      fallbackItems: previewItems,
      history: superSearchHistory,
      pinnedCompanyIds,
      pinnedFirmIds,
    });

    if (quickAccessItems.length === 0) {
      return [];
    }

    return [
      {
        id: 'quick-access',
        items: quickAccessItems,
        label: normalizedQuery.length === 0 ? 'Quick access' : 'Other destinations',
      },
    ];
  }, [
    actionVisibility.bulkActions,
    actionVisibility.excelExport,
    actionVisibility.pdfExport,
    activeMeasurementDate,
    availableTours,
    canAccessAuditLogs,
    canAccessFirmSettings,
    canAccessUserManagement,
    canCreateCompany,
    canCreateMeasurementDate,
    canMutateCompany,
    canOpenProForma,
    companyList,
    context,
    initialContext.firm,
    pendingPage,
    pinnedCompanyIds,
    pinnedFirmIds,
    quickAccessPageTypes,
    quickAccessResultIds,
    quickAccessResults,
    normalizedQuery.length,
    superSearchHistory,
  ]);
  const isWaitingForRemoteSearch =
    open && isQueryDebouncing && normalizedQuery.length >= SUPER_SEARCH_MIN_REMOTE_SEARCH_LENGTH;
  const isSearching = isWaitingForRemoteSearch || (isSearchEnabled && search.isFetching);
  const isLoading = isSearching && matchingItems.length === 0;
  const { hasFatalError, hasPartialError, partialErrorDescription, searchErrorDescription } = useSuperSearchErrorState({
    error: search.error,
    hasResults: matchingItems.length > 0,
    includeDocuments,
    includeSavedDestinations,
    isEnabled: isSearchEnabled,
    isError: search.isError,
  });
  const canShowSuggestedDestinations = !pendingPage && !isLoading && !hasFatalError && quickAccessSections.length > 0;
  const isShowingQuickAccess = canShowSuggestedDestinations && normalizedQuery.length === 0;
  const isShowingOtherDestinations =
    canShowSuggestedDestinations && normalizedQuery.length > 0 && matchingItems.length === 0;
  const isShowingSuggestedDestinations = isShowingQuickAccess || isShowingOtherDestinations;
  const visibleSections = isShowingSuggestedDestinations ? quickAccessSections : matchingSections;
  const items = useMemo(() => visibleSections.flatMap(section => section.items), [visibleSections]);
  const previousItemsRef = useRef(items);
  const itemToneById = useMemo(
    () =>
      new Map(
        visibleSections.flatMap(section =>
          section.items.map(item => [item.id, getResultTone(item, section.id)] as const)
        )
      ),
    [visibleSections]
  );
  const selectedItem = items[selectedIndex];
  const ghostCompletion = getGhostCompletion(query, selectedItem);
  let tabActionLabel: 'complete' | 'set scope' | undefined;
  if (selectedItem && canScopeItem(selectedItem, pendingPage)) {
    tabActionLabel = 'set scope';
  } else if (selectedItem && ghostCompletion) {
    tabActionLabel = 'complete';
  }
  const isContextDimmed = Boolean(
    selectedItem?.isOutsideContext || (items.length > 0 && items.every(item => item.isOutsideContext))
  );

  // Keep the active result stable when remote sections are appended after the local preview.
  useEffect(() => {
    const previousItems = previousItemsRef.current;
    previousItemsRef.current = items;
    setSelectedIndex(currentIndex => {
      if (currentIndex < 0) {
        return -1;
      }

      const previousSelectedId = previousItems[currentIndex]?.id;
      if (previousSelectedId) {
        return items.findIndex(item => item.id === previousSelectedId);
      }

      return currentIndex < items.length ? currentIndex : -1;
    });
  }, [items]);

  const handleRetrySearch = useCallback(() => {
    searchInputRef.current?.focus();
    search.refetch().catch(() => undefined);
  }, [search]);
  const handleBroadenSearch = useCallback(() => {
    if (context.company && context.firm) {
      setContext({ firm: context.firm });
    } else if (context.firm) {
      setContext({});
    }

    setSelectedIndex(-1);
    setIsKeyboardNavigation(false);
    window.requestAnimationFrame(() => searchInputRef.current?.focus());
  }, [context.company, context.firm]);
  const handleClearSearch = useCallback(() => {
    setQuery('');
    setSelectedIndex(-1);
    setIsKeyboardNavigation(false);
    window.requestAnimationFrame(() => searchInputRef.current?.focus());
  }, []);
  const handleClearHistory = useCallback(() => {
    clearSuperSearchHistory();
    setSelectedIndex(-1);
    setIsKeyboardNavigation(false);
    window.requestAnimationFrame(() => searchInputRef.current?.focus());
  }, [clearSuperSearchHistory]);

  // Move focus to the only available action while delayed navigation remains cancellable.
  useEffect(() => {
    if (pendingNavigation) {
      navigationCancelRef.current?.focus();
    }
  }, [pendingNavigation]);

  const handleClose = useCallback(() => {
    cancelPendingNavigation();
    onClose();
  }, [cancelPendingNavigation, onClose]);

  const handleCancelNavigation = useCallback(() => {
    cancelPendingNavigation();
    window.requestAnimationFrame(() => searchInputRef.current?.focus());
  }, [cancelPendingNavigation]);

  const handleDialogClose: NonNullable<DialogProps['onClose']> = useCallback(
    (_event, reason) => {
      if (reason !== 'escapeKeyDown' || !pendingNavigation) {
        handleClose();
        return;
      }

      handleCancelNavigation();
    },
    [handleCancelNavigation, handleClose, pendingNavigation]
  );

  const handleNavigate = useCallback(
    (item: SuperSearchDisplayItem) => {
      const targetCompany = item.result.company
        ? (companyList as FirmCompany[] | null)?.find(company => company.company_id === item.result.company?.id)
        : undefined;
      const targetCompanyMeasurementDates = targetCompany?.company_measurement_dates ?? [];
      const eligibleMeasurementDates =
        item.page?.key === VALUATIONS_SLUG
          ? targetCompanyMeasurementDates.filter(({ allocation_ids }) => Boolean(allocation_ids?.length))
          : targetCompanyMeasurementDates;
      const latestTargetMeasurementDate = eligibleMeasurementDates.reduce<string | undefined>(
        (latestDate, { date }) => (!latestDate || date > latestDate ? date : latestDate),
        undefined
      );
      const actionMeasurementDate = item.companyAction
        ? (eligibleMeasurementDates.find(({ date }) => date === activeMeasurementDate)?.date ??
          latestTargetMeasurementDate ??
          activeMeasurementDate)
        : undefined;
      const path = getSuperSearchNavigationPath({
        currentPathname: location.pathname,
        currentSearch: location.search,
        fallbackMeasurementDate: actionMeasurementDate,
        initialContext,
        item,
      });
      cancelPendingNavigation();
      setPendingNavigation({
        context: getNavigationContext(item),
        label: item.label,
        tone: itemToneById.get(item.id) ?? getResultTone(item, ''),
        typeLabel: getResultTypeLabel(item),
      });
      navigationTimerRef.current = window.setTimeout(() => {
        navigationTimerRef.current = null;
        recordSuperSearchHistoryItem(item);
        handleClose();
        navigate(path);
      }, SUPER_SEARCH_NAVIGATION_DELAY_MS);
    },
    [
      activeMeasurementDate,
      cancelPendingNavigation,
      companyList,
      handleClose,
      initialContext,
      itemToneById,
      location.pathname,
      location.search,
      navigate,
      recordSuperSearchHistoryItem,
    ]
  );

  const handleScopeItem = useCallback(
    (item: SuperSearchDisplayItem) => {
      if (item.kind === 'company-page-command' && item.page) {
        setPendingPage({
          key: item.page.key,
          label: item.page.label,
          scope: SuperSearchPageResult.scope.COMPANY,
        });
        setQuery('');
        setSelectedIndex(-1);
        return true;
      }

      if (item.kind === 'firm-page-command' && item.page) {
        setPendingPage({
          key: item.page.key,
          label: item.page.label,
          scope: SuperSearchPageResult.scope.FIRM,
        });
        setQuery('');
        setSelectedIndex(-1);
        return true;
      }

      if (item.result.type === SuperSearchResult.type.FIRM) {
        setContext({ firm: item.result.firm });
        setPendingPage(undefined);
        setQuery('');
        setSelectedIndex(-1);
        return true;
      }

      if (item.result.type === SuperSearchResult.type.COMPANY && item.result.company) {
        setContext({ company: item.result.company, firm: item.result.firm });
        setPendingPage(undefined);
        setQuery('');
        setSelectedIndex(-1);
        return true;
      }

      if (pendingPage?.scope === SuperSearchPageResult.scope.FIRM) {
        setContext({ firm: item.result.firm });
        setQuery('');
        setSelectedIndex(-1);
        return true;
      }

      if (pendingPage?.scope === SuperSearchPageResult.scope.COMPANY && item.result.company) {
        setContext({ company: item.result.company, firm: item.result.firm });
        setQuery('');
        setSelectedIndex(-1);
        return true;
      }

      return false;
    },
    [pendingPage]
  );

  const handleBaseAction = useSuperSearchActions({
    onClose: handleClose,
    onExecute: recordSuperSearchHistoryItem,
    onNavigate: handleNavigate,
    onScopeItem: handleScopeItem,
    onStartTour: startTour,
  });
  const handleActivateItem = useCallback(
    (item: SuperSearchDisplayItem) => {
      if (item.kind === 'company-action' && item.companyAction && item.page && !context.company) {
        setPendingPage({
          companyAction: item.companyAction,
          key: item.page.key,
          label: item.label,
          searchLabel: item.page.label,
          scope: SuperSearchPageResult.scope.COMPANY,
        });
        setQuery('');
        setSelectedIndex(-1);
        return;
      }

      handleBaseAction(item);
    },
    [context.company, handleBaseAction]
  );

  const removeLastContext = useCallback(() => {
    setSelectedIndex(-1);

    if (pendingPage) {
      setPendingPage(undefined);
      return;
    }

    if (context.company) {
      setContext(currentContext => ({ firm: currentContext.firm }));
      return;
    }

    if (context.firm) {
      setContext({});
    }
  }, [context.company, context.firm, pendingPage]);

  const handleKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Escape') {
        flashShortcut('close');
        event.preventDefault();
        event.stopPropagation();

        if (pendingNavigation) {
          handleCancelNavigation();
          return;
        }

        handleClose();
        return;
      }

      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        flashShortcut('navigate');
        event.preventDefault();
        const isStartingKeyboardNavigation = !isKeyboardNavigation;
        setIsKeyboardNavigation(true);
        setSelectedIndex(currentIndex => {
          if (items.length === 0) {
            return -1;
          }

          if (isStartingKeyboardNavigation || currentIndex === -1) {
            return event.key === 'ArrowDown' ? 0 : items.length - 1;
          }

          const direction = event.key === 'ArrowDown' ? 1 : -1;
          return (currentIndex + direction + items.length) % items.length;
        });
        return;
      }

      if (event.key === 'Enter' && selectedItem) {
        flashShortcut('go');
        event.preventDefault();
        handleActivateItem(selectedItem);
        return;
      }

      if (event.key === 'Tab' && !event.shiftKey) {
        event.preventDefault();

        if (selectedItem && handleScopeItem(selectedItem)) {
          flashShortcut('setScope');
          return;
        }

        if (ghostCompletion && selectedItem) {
          flashShortcut('setScope');
          setQuery(selectedItem.label);
        }
        return;
      }

      if (event.key === 'Backspace' && query === '') {
        flashShortcut('popScope');
        removeLastContext();
      }
    },
    [
      ghostCompletion,
      flashShortcut,
      handleActivateItem,
      handleCancelNavigation,
      handleClose,
      handleScopeItem,
      isKeyboardNavigation,
      items.length,
      pendingNavigation,
      query,
      removeLastContext,
      selectedItem,
    ]
  );

  const handleContextClick = useCallback((scope: 'company' | 'firm') => {
    if (scope === 'firm') {
      setContext(currentContext => ({ firm: currentContext.firm }));
    }

    setPendingPage(undefined);
    setQuery('');
    setSelectedIndex(-1);
  }, []);

  return (
    <PrismThemeProvider>
      <StyledDialog
        aria-label="Super Search"
        data-testid={SUPER_SEARCH_TEST_IDS.dialog}
        fullWidth
        isNavigating={Boolean(pendingNavigation)}
        maxWidth={false}
        onClose={handleDialogClose}
        open={open}
        zoomLevel={zoomLevel}>
        {isSearching && (
          <LoadingProgress aria-label="Loading Super Search results" data-testid={SUPER_SEARCH_TEST_IDS.loading} />
        )}
        {pendingNavigation && (
          <SuperSearchNavigationStatus
            cancelButtonRef={navigationCancelRef}
            navigation={pendingNavigation}
            onCancel={handleCancelNavigation}
            pressedShortcut={pressedShortcut}
          />
        )}
        <SuperSearchHeader
          context={context}
          ghostCompletion={ghostCompletion}
          isContextDimmed={isContextDimmed}
          onBlur={() => setPressedShortcut(undefined)}
          onContextClick={handleContextClick}
          onKeyDown={handleKeyDown}
          onQueryChange={nextQuery => {
            setQuery(nextQuery);
            setSelectedIndex(-1);
            setIsKeyboardNavigation(false);
          }}
          open={open}
          pendingPage={pendingPage}
          query={query}
          searchInputRef={searchInputRef}
          selectedItem={selectedItem}
        />

        <SuperSearchResults
          context={context}
          firmIconUrlsById={firmIconUrlsById}
          hasFatalError={hasFatalError}
          hasPartialError={hasPartialError}
          isKeyboardNavigation={isKeyboardNavigation}
          isLoading={isLoading}
          isSearching={isSearching}
          isShowingSuggestedDestinations={isShowingSuggestedDestinations}
          matchingItemCount={matchingItems.length}
          onActivateItem={handleActivateItem}
          onBroadenSearch={handleBroadenSearch}
          onClearSearch={handleClearSearch}
          onRetrySearch={handleRetrySearch}
          onSelectItem={itemIndex => {
            setIsKeyboardNavigation(false);
            setSelectedIndex(itemIndex);
          }}
          partialErrorDescription={partialErrorDescription}
          pendingPage={pendingPage}
          query={query}
          searchErrorDescription={searchErrorDescription}
          selectedIndex={selectedIndex}
          visibleSections={visibleSections}
        />

        <SuperSearchFooter
          canClearHistory={isShowingQuickAccess && isSuperSearchHistoryReady && superSearchHistory.entries.length > 0}
          context={context}
          onClearHistory={handleClearHistory}
          pressedShortcut={pressedShortcut}
          tabActionLabel={tabActionLabel}
        />
      </StyledDialog>
    </PrismThemeProvider>
  );
};

export default SuperSearch;
